<script lang="ts">
  // 4 · Billing period: weekly, monthly, quarterly or a custom cycle.
  import { fly } from 'svelte/transition';
  import { CalendarDays, CalendarRange, CalendarClock, Settings2 } from '@lucide/svelte';
  import { t } from '../../i18n.svelte';
  import { account } from '../../stores/account.svelte';
  import { resolvePeriod } from '../../periods';
  import { ChoiceCards, Field, Input, Segmented, type ChoiceOption } from '../../ui';
  import StepIntro from './StepIntro.svelte';
  import { flow, type PeriodChoice } from '../state.svelte';

  const ICONS = { weekly: CalendarDays, monthly: CalendarRange, quarterly: CalendarClock, custom: Settings2 };
  const options = $derived<ChoiceOption<PeriodChoice>[]>(
    (['weekly', 'monthly', 'quarterly', 'custom'] as const).map((value) => ({
      value,
      label: t(`onb.period.${value}`),
      description: t(`onb.period.${value}_text`),
      icon: ICONS[value]
    }))
  );

  const preview = $derived.by(() => {
    try {
      return resolvePeriod('current_period', { work: { ...account.work, ...flow.periodPatch() } }).label;
    } catch {
      return '';
    }
  });
</script>

<StepIntro kicker={t('onb.period.kicker')} title={t('onb.period.title')} lead={t('onb.period.text')} />
<ChoiceCards bind:value={flow.periodChoice} {options} cols={2} ariaLabel={t('onb.period.title')} />

{#if flow.periodChoice === 'custom'}
  <div class="custom" transition:fly={{ y: flow.still ? 0 : -6, duration: flow.still ? 0 : 180 }}>
    <Field label={t('onb.period.every')} for="onb-every">
      <div class="every">
        <Input id="onb-every" type="number" min="1" max="52" bind:value={flow.customEvery} />
        <Segmented
          value={flow.customUnit}
          options={[{ value: 'weeks', label: t('onb.period.unit_weeks') }, { value: 'months', label: t('onb.period.unit_months') }]}
          size="sm"
          ariaLabel={t('onb.period.every')}
          onchange={(v) => (flow.customUnit = v)}
        />
      </div>
    </Field>
    <Field label={t('onb.period.from')} for="onb-from">
      <Input id="onb-from" type="date" bind:value={flow.customFrom} />
    </Field>
  </div>
{/if}

{#if preview}
  <p class="preview"><CalendarRange size={15} /> {t('onb.period.current', { range: preview })}</p>
{/if}

<style>
  .custom {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: var(--space-3);
    margin-top: var(--space-3);
    padding: var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface-2);
  }
  .every { display: grid; grid-template-columns: 72px auto; align-items: center; gap: var(--space-2); }
  .preview { display: flex; align-items: center; gap: var(--space-2); margin-top: var(--space-3); font-size: var(--text-sm); font-weight: var(--weight-medium); color: var(--text-2); }
</style>
